'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import Image from '@components/common/ImageFallback';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { getContentsInTopics } from '@root/src/services/client/topic';
import Link from 'next/link';

interface Content {
  id: string | number;
  title: string;
  slug: string;
  description?: string;
  image?: string[] | string;
  createdAt?: string;
  publishedAt?: string;
  author?: string;
}

interface Topic {
  id: string | number;
  name: string;
  slug: string;
  description?: string;
  image?: string[] | string;
  author?: string;
}

interface TopicContentsListProps {
  topicParent: Topic;
  listContentInitial: Content[];
  currentPage?: number;
  totalPage?: number;
}

const ITEMS_PER_PAGE = 6;

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('vi-VN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const getImageUrl = (image: string[] | string | undefined) => {
  if (!image) return null;
  if (Array.isArray(image)) return image.find(Boolean) || null;
  return image;
};

const getDisplayDate = (content: Content) => content.publishedAt || content.createdAt;

type PaginationItem = number | 'start-ellipsis' | 'end-ellipsis';

const getPaginationItems = (currentPage: number, totalPage: number) => {
  if (totalPage <= 7) {
    return Array.from({ length: totalPage }, (_, index) => index + 1);
  }

  const items: PaginationItem[] = [1];
  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPage - 1, currentPage + 1);

  if (startPage > 2) {
    items.push('start-ellipsis');
  }

  for (let page = startPage; page <= endPage; page += 1) {
    items.push(page);
  }

  if (endPage < totalPage - 1) {
    items.push('end-ellipsis');
  }

  items.push(totalPage);
  return items;
};

export default function TopicContentsList({
  topicParent,
  listContentInitial,
  currentPage = 1,
  totalPage = 1,
}: TopicContentsListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [contents, setContents] = useState<Content[]>(listContentInitial || []);
  const [page, setPage] = useState(currentPage);
  const [pageCount, setPageCount] = useState(Math.max(totalPage || 1, 1));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setContents(listContentInitial || []);
    setPage(currentPage);
    setPageCount(Math.max(totalPage || 1, 1));
  }, [currentPage, listContentInitial, totalPage]);

  const paginationItems = useMemo(
    () => getPaginationItems(page, pageCount),
    [page, pageCount]
  );

  const handlePageChange = async (nextPage: number) => {
    if (
      nextPage === page ||
      nextPage < 1 ||
      nextPage > pageCount ||
      isLoading
    ) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await getContentsInTopics(
        topicParent.slug,
        nextPage,
        undefined,
        undefined,
        undefined,
        ITEMS_PER_PAGE
      );

      if (res?.success) {
        setContents(res.data || []);
        setPage(nextPage);
        setPageCount(Math.max(res.totalPage || pageCount, 1));
        requestAnimationFrame(() => {
          listRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      } else {
        setContents([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div ref={listRef}>
      <div className="mb-8 border-b border-[#EDE6D8] pb-6">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#7F6B48]">
          Chuyên đề
        </p>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <h1 className="text-3xl font-bold leading-tight text-[#223A27] md:text-4xl">
              {topicParent.name}
            </h1>
            {topicParent.description && (
              <p className="mt-3 text-base leading-7 text-[#666]">
                {topicParent.description}
              </p>
            )}
          </div>

          {pageCount > 1 && (
            <div className="w-fit rounded-lg border border-[#E6D9C5] bg-[#FFFDF9] px-4 py-3 text-sm font-semibold text-[#7F6B48]">
              Trang {page}/{pageCount}
            </div>
          )}
        </div>
      </div>

      {!contents.length ? (
        <div className="rounded-lg border border-[#E6D9C5] bg-[#FFFDF9] px-6 py-16 text-center">
          <FileTextOutlined className="text-4xl text-[#8A6B45]" />
          <p className="mt-4 text-[#666]">
            Không có bài viết trong chuyên đề này
          </p>
        </div>
      ) : (
        <>
          <div
            className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 ${
              isLoading ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            {contents.map((content, index) => {
              const imageUrl =
                getImageUrl(content.image) || getImageUrl(topicParent.image);
              const author = content.author || topicParent.author;

              return (
                <Link
                  key={content.id}
                  href={`/topic/${topicParent.slug}/${content.slug}`}
                  className="group"
                >
                  <article className="flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-[#EDE6D8] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#D4C4A8] hover:shadow-lg">
                    <div className="relative h-44 overflow-hidden bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8] 2xl:h-40">
                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt={content.title}
                          fill
                          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                          className="object-cover transition duration-500 group-hover:scale-105"
                          isLCP={page === 1 && index === 0}
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-[#223A27]/20">
                          <FileTextOutlined className="text-5xl" />
                        </div>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col gap-3 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-medium text-[#7F6B48]">
                        {getDisplayDate(content) && (
                          <span className="inline-flex items-center gap-1.5">
                            <CalendarOutlined />
                            {formatDate(getDisplayDate(content) || '')}
                          </span>
                        )}
                        {author && (
                          <span className="inline-flex items-center gap-1.5">
                            <UserOutlined />
                            {author}
                          </span>
                        )}
                      </div>

                      <h3 className="truncate text-lg font-semibold leading-6 text-[#223A27] transition-colors group-hover:text-[#1a2b1f]">
                        {content.title}
                      </h3>

                      {content.description && (
                        <p className="text-sm leading-6 text-[#666] line-clamp-3">
                          {content.description}
                        </p>
                      )}

                      <div className="mt-auto border-t border-[#E8DCC8] pt-3">
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#223A27] transition-colors group-hover:text-[#7F6B48]">
                          Đọc thêm
                          <ArrowRightOutlined />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>

          {isLoading && (
            <div className="mt-5 text-center text-sm font-semibold text-[#8A6B45]">
              Đang tải nội dung...
            </div>
          )}

          {pageCount > 1 && (
            <nav
              className="mt-10 flex flex-wrap items-center justify-center gap-2"
              aria-label="Phân trang bài viết"
            >
              <button
                type="button"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1 || isLoading}
                className="flex h-10 min-w-10 items-center justify-center rounded-lg border border-[#D9C8AD] bg-white px-3 text-sm font-semibold text-[#4F463C] transition hover:border-[#223A27] hover:text-[#223A27] disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Trang trước"
              >
                <LeftOutlined />
              </button>

              {paginationItems.map(item =>
                typeof item === 'number' ? (
                  <button
                    key={item}
                    type="button"
                    onClick={() => handlePageChange(item)}
                    disabled={isLoading}
                    className={`flex h-10 min-w-10 items-center justify-center rounded-lg border px-3 text-sm font-semibold transition ${
                      page === item
                        ? 'border-[#223A27] bg-[#223A27] text-white'
                        : 'border-[#D9C8AD] bg-white text-[#4F463C] hover:border-[#223A27] hover:text-[#223A27]'
                    }`}
                    aria-current={page === item ? 'page' : undefined}
                  >
                    {item}
                  </button>
                ) : (
                  <span
                    key={item}
                    className="flex h-10 min-w-10 items-center justify-center text-sm font-semibold text-[#8A6B45]"
                  >
                    ...
                  </span>
                )
              )}

              <button
                type="button"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount || isLoading}
                className="flex h-10 min-w-10 items-center justify-center rounded-lg border border-[#D9C8AD] bg-white px-3 text-sm font-semibold text-[#4F463C] transition hover:border-[#223A27] hover:text-[#223A27] disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Trang sau"
              >
                <RightOutlined />
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
